import { getOptimizelyVisitorId, getExperimentsData, initOptimizelyUserInfo } from '@busuu/experiments';

/**
 * Currently the SP implementation fetches the library from our CDN
 * This is because their CDN had 'analytics' & 'snowplow' in the script title + path
 * and this really played badly with adblockers :D
 * Moving forwards we should migrate to the new Snowplow SDK dependency and upgrade.
 * This will need approving/scoping with the data team as they fully expect us to use v2.12.0 for now.
 */
var SP_SDK_LOCATION = 'https://cdn.busuu.com/busuu-v2.12.0.js';
var SP_SCHEMA = 'iglu:com.busuu/standard_event/jsonschema/1-0-0';
var SP_ENDPOINTS = {
    PRODUCTION: 'events.busuu.com',
    INTEGRATION: 'events-integration.busuu.com',
};
/**
 * Imported into the clients directly
 * and passed down to the init() function
 */
var SP_APP_CONFIG = {
    /** Marketing platform, small parts to be migrated into `busuu_frontend` */
    HOMEPAGE: {
        name: 'busuu_homepage_tracker',
        appId: 'busuu_homepage',
    },
    /** Current course app, to be migrated into `busuu_frontend` */
    WEBAPP: {
        name: 'busuu_webapp_tracker',
        appId: 'busuu_webapp',
    },
    /** Management platform (B2B) */
    ORG: {
        name: 'busuu_mp_tracker',
        appId: 'busuu_mp',
    },
    /** Current Payments/Paywall app, to be migrated into `busuu_frontend` */
    PAYMENTS: {
        name: 'busuu_web_payments_tracker',
        appId: 'web_payments',
    },
    /** B2C Web application, will combine Courses, Payments, Auth etc... */
    FRONTEND: {
        name: 'busuu_frontend_tracker',
        appId: 'busuu_frontend',
    },
};

/* eslint-disable */
/**
 * Their documentation recommend asyncronous integration over syncronous
 *
 * https://github.com/snowplow/snowplow/wiki/integrating-javascript-tags-onto-your-website
 * We initially tried splitting their script so that we could queue events and wait until
 * DOM load before injecting their script. However this caused errors and prevented the
 * custom event tracker from occasionally being fired so we opted to go with their
 * recommended initialisation script.
 *
 * See: https://github.com/snowplow/snowplow/issues/3169
 * https://github.com/snowplow/snowplow/wiki/1-General-parameters-for-the-Javascript-tracker
 */
var loadSnowplowScript = function (_a) {
    var config = _a.config, endpoint = _a.endpoint;
    (function (p, l, o, w, i, n, g) {
        if (!p[i]) {
            p.GlobalSnowplowNamespace = p.GlobalSnowplowNamespace || [];
            p.GlobalSnowplowNamespace.push(i);
            p[i] = function () {
                (p[i].q = p[i].q || []).push(arguments);
            };
            p[i].q = p[i].q || [];
            n = l.createElement(o);
            g = l.getElementsByTagName(o)[0];
            n.async = 1;
            n.src = w;
            g.parentNode.insertBefore(n, g);
        }
    })(window, document, 'script', SP_SDK_LOCATION, 'snowplow');
    window.snowplow('newTracker', config.name, endpoint, {
        forceSecureTracker: true,
        discoverRootDomain: true,
        userFingerprint: true,
        appId: config.appId,
    });
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

/**
 * Gets the role of the user.
 * If he has an institution he's part of busuuPRO and we return 'b2b'
 * @param {Object} user
 * @return {String}
 */
var getUserRole = function (userProps) {
    if (!userProps)
        return 'free'; // Failsafe, in case somehow the props are missing
    var userRole = userProps.isPremium ? 'premium' : 'free';
    if (userProps.institution)
        userRole = 'b2b';
    return userRole;
};
var buildSnowplowEventData = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var eventName = _a.eventName, _k = _a.data, data = _k === void 0 ? {} : _k, userProps = _a.userProps, version = _a.version;
    return {
        platform: 'web',
        version: version,
        user_agent: (_c = (_b = window === null || window === void 0 ? void 0 : window.navigator) === null || _b === void 0 ? void 0 : _b.userAgent) !== null && _c !== void 0 ? _c : '',
        uid: (_e = (_d = userProps === null || userProps === void 0 ? void 0 : userProps.uid) === null || _d === void 0 ? void 0 : _d.toString()) !== null && _e !== void 0 ? _e : '',
        language_learnt: (_f = userProps === null || userProps === void 0 ? void 0 : userProps.langCourse) !== null && _f !== void 0 ? _f : '',
        interface_language: (_g = userProps === null || userProps === void 0 ? void 0 : userProps.langInterface) !== null && _g !== void 0 ? _g : '',
        role: (_h = getUserRole(userProps)) !== null && _h !== void 0 ? _h : '',
        event: eventName,
        ts: Math.floor(new Date().getTime() / 1000),
        params: __assign({ optimizelyVisitorId: getOptimizelyVisitorId(), country: (_j = userProps === null || userProps === void 0 ? void 0 : userProps.countryCode) !== null && _j !== void 0 ? _j : '', optimizely_experiments_current: getExperimentsData() }, data),
    };
};

// Consts
var FILENAME = 'snowplow.service.ts';
/**
 * Returns true if Snowplow is present
 * @returns {boolean}
 */
var isSnowplowLoaded = function () {
    var loaded = Boolean(window.snowplow);
    if (!loaded)
        window.console.log(FILENAME, 'Snowplow not initialized in the global window.');
    return loaded;
};
/**
 *
 * NB: this code is the recommended code provided by SP. Please do not edit the regex.
 * Function to extract the Snowplow Domain User Information from the first-party cookie set by the Snowplow JavaScript Tracker
 * https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/javascript-tracker/cookies-local-storage/getting-the-userid-from-the-snowplow-cookie/
 *
 * @param {string} name (optional) The value used for "name" in the tracker constructor argmap (leave blank if you did not set a custom cookie name)
 * @return {string} or bool The ID string if the cookie exists or false if the cookie has not been set yet
 *
 */
var getSnowplowDuid = function (name) {
    if (name === void 0) { name = ''; }
    var onError = function () { return ({
        domain_userid: '',
        domain_sessionidx: '',
        domain_sessionid: '',
    }); };
    try {
        var cookieName = name || '_sp_';
        var matcher = new RegExp(cookieName + 'id\\.[a-f0-9]+=([^;]+);?');
        var match = matcher.exec(window.document.cookie);
        var split = match && match[1] && match[1].split('.');
        if (match && match[1] && split) {
            return {
                domain_userid: split[0] || '',
                domain_sessionidx: split[2] || '',
                domain_sessionid: split[5] || '',
            };
        }
        else {
            return onError();
        }
    }
    catch (e) {
        return onError();
    }
};
/**
 * Sends event to Snowplow
 * NB: `user` is optional
 */
var sendSnowplowEvent = function (_a, isLocal) {
    var eventName = _a.eventName, data = _a.data, userProps = _a.userProps, version = _a.version;
    if (isLocal === void 0) { isLocal = false; }
    if (!isSnowplowLoaded())
        return false;
    var snowplowData = buildSnowplowEventData({ eventName: eventName, data: data, userProps: userProps, version: version });
    if (!isLocal) {
        void window.snowplow('trackSelfDescribingEvent', {
            schema: SP_SCHEMA,
            data: snowplowData,
        });
    }
    else {
        window.console.log(FILENAME, 'Local - Not sending event: ', snowplowData);
    }
};
var identifySnowplowUser = function (userId) {
    if (!isSnowplowLoaded())
        return false;
    void window.snowplow('setUserId', userId);
};

/**
 * Ties Optimizely + Snowplow together (as they depend on each other)
 * and ensures that everything is set up before we send events.
 */
var initBusuuTracking = function (_a) {
    var _b, _c;
    var config = _a.config, endpoint = _a.endpoint, userProps = _a.userProps;
    // Get userUid for Snowplow
    var userUid = (_c = (_b = userProps === null || userProps === void 0 ? void 0 : userProps.uid) === null || _b === void 0 ? void 0 : _b.toString()) !== null && _c !== void 0 ? _c : '';
    // Load Snowplow
    loadSnowplowScript({ config: config, endpoint: endpoint });
    // Identify users
    initOptimizelyUserInfo(userProps);
    identifySnowplowUser(userUid);
};

export { SP_APP_CONFIG, SP_ENDPOINTS, SP_SCHEMA, SP_SDK_LOCATION, getSnowplowDuid, identifySnowplowUser, initBusuuTracking, isSnowplowLoaded, loadSnowplowScript, sendSnowplowEvent };
//# sourceMappingURL=index.esm.js.map
